import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../services/api';

const formatDt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
};

const shortUserAgent = (ua) => {
  if (!ua) return 'Navegador desconocido';
  const s = ua.trim();
  if (/iPhone|iPad|iPod/i.test(s)) return 'iPhone / iPad (Safari)';
  if (/Android/i.test(s)) return 'Android';
  if (/Chrome/i.test(s) && !/Edg/i.test(s)) return 'Chrome';
  if (/Safari/i.test(s) && !/Chrome/i.test(s)) return 'Safari';
  if (/Firefox/i.test(s)) return 'Firefox';
  if (s.length > 72) return `${s.slice(0, 72)}…`;
  return s;
};

const SupervisorDevices = () => {
  const [supervisorFactories, setSupervisorFactories] = useState([]);
  const [factoryId, setFactoryId] = useState('');
  const [employeeUserId, setEmployeeUserId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await attendanceAPI.myFactories();
        const all = res.data || [];
        setSupervisorFactories(all);
      } catch {
        setSupervisorFactories([]);
      }
    })();
  }, []);

  const loadRows = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const params = {};
      if (factoryId) params.factory_id = Number(factoryId);
      if (employeeUserId) params.employee_user_id = Number(employeeUserId);
      const res = await attendanceAPI.supervisorTeamDevices(params);
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudieron cargar los dispositivos');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [factoryId, employeeUserId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const employeeOptions = useMemo(() => {
    return rows
      .map((r) => ({ id: r.employee_user_id, name: r.employee_name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  useEffect(() => {
    if (!employeeUserId) return;
    if (!employeeOptions.some((o) => String(o.id) === String(employeeUserId))) {
      setEmployeeUserId('');
    }
  }, [employeeUserId, employeeOptions]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.employee_name?.toLowerCase().includes(q) ||
        r.employee_email?.toLowerCase().includes(q) ||
        (r.factory_names || []).some((n) => n.toLowerCase().includes(q))
    );
  }, [rows, search]);

  const totalDevices = useMemo(
    () => filteredRows.reduce((acc, r) => acc + (r.devices?.length || 0), 0),
    [filteredRows]
  );

  return (
    <div className="space-y-4 pb-6">
      <p className="text-sm text-gray-600">
        Teléfonos y navegadores donde cada persona inició sesión en SiCheck. Un empleado con más de un
        dispositivo puede haber cambiado de celular o usar varios equipos.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex-1 min-w-[140px]">
          <label className="form-label-mobile text-xs">Sede</label>
          <select
            className="input-mobile w-full"
            value={factoryId}
            onChange={(e) => setFactoryId(e.target.value)}
          >
            <option value="">Todas mis sedes</option>
            {supervisorFactories.map((f) => (
              <option key={f.id} value={String(f.id)}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="form-label-mobile text-xs">Empleado</label>
          <select
            className="input-mobile w-full"
            value={employeeUserId}
            onChange={(e) => setEmployeeUserId(e.target.value)}
          >
            <option value="">Todos</option>
            {employeeOptions.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="form-label-mobile text-xs">Buscar</label>
          <input
            type="search"
            className="input-mobile w-full"
            placeholder="Nombre o email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button type="button" className="btn-mobile btn-secondary min-h-[44px]" onClick={loadRows}>
          Actualizar
        </button>
      </div>

      {err && <div className="alert-mobile error">{err}</div>}

      <p className="text-xs text-gray-500">
        {loading
          ? 'Cargando…'
          : `${filteredRows.length} persona(s) · ${totalDevices} dispositivo(s) registrado(s)`}
      </p>

      {!loading && filteredRows.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-gray-600">
          No hay personas bajo tu supervisión con los filtros actuales, o aún no iniciaron sesión desde
          ningún dispositivo.
        </div>
      )}

      <div className="space-y-3">
        {filteredRows.map((person) => (
          <article
            key={person.employee_user_id}
            className="rounded-xl border border-slate-200 bg-white overflow-hidden"
          >
            <header className="px-4 py-3 bg-slate-50 border-b border-slate-100">
              <h3 className="font-semibold text-gray-900">{person.employee_name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{person.employee_email}</p>
              {person.factory_names?.length > 0 && (
                <p className="text-xs text-teal-800 mt-1">
                  Sedes: {person.factory_names.join(' · ')}
                </p>
              )}
            </header>
            <div className="px-4 py-3">
              {!person.devices?.length ? (
                <p className="text-sm text-gray-500">Sin dispositivos registrados todavía.</p>
              ) : (
                <ul className="space-y-3">
                  {person.devices.map((d) => (
                    <li
                      key={`${person.employee_user_id}-${d.device_id}`}
                      className="rounded-lg border border-slate-100 bg-slate-50/80 p-3 text-sm"
                    >
                      <p className="font-medium text-gray-900">{shortUserAgent(d.user_agent)}</p>
                      <dl className="mt-2 grid gap-1 text-xs text-gray-600 sm:grid-cols-2">
                        <div>
                          <dt className="text-gray-400">Último login</dt>
                          <dd>{formatDt(d.last_login_at)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Logins</dt>
                          <dd>{d.login_count}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Primera vez</dt>
                          <dd>{formatDt(d.first_seen_at)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Última actividad</dt>
                          <dd>{formatDt(d.last_seen_at)}</dd>
                        </div>
                        {d.ip_address && (
                          <div className="sm:col-span-2">
                            <dt className="text-gray-400">IP (último login)</dt>
                            <dd>{d.ip_address}</dd>
                          </div>
                        )}
                      </dl>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default SupervisorDevices;
